import React, { useEffect, useMemo, useState } from 'react';
import { FaTimes, FaBoxOpen, FaBuilding, FaFileAlt, FaArrowDown, FaArrowUp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import './StockRecordForms.css';

const CREATE = `
  mutation CreateStockMovement($createInventoryManagementInput: CreateInventoryManagementInput!) {
    createInventoryManagement(createInventoryManagementInput: $createInventoryManagementInput) {
      id
    }
  }
`;

const UPDATE = `
  mutation UpdateStockMovement($updateInventoryManagementInput: UpdateInventoryManagementInput!) {
    updateInventoryManagement(updateInventoryManagementInput: $updateInventoryManagementInput) {
      id
    }
  }
`;

const FIND_ITEMS = `
  query StockFormItems {
    findAllItem {
      id name code quantity available_quantity perItemCost unit supplier
    }
  }
`;

const USERS = `
  query StockFormUsers {
    getAllUser(page: 1, limit: 100) {
      data { id firstName lastName email }
    }
  }
`;

const WAREHOUSE = 'Main Warehouse';

export function encodeMovementMeta(meta, note) {
  return `[SASMETA]${JSON.stringify(meta || {})}\n${note || ''}`.trim();
}

export function decodeMovementMeta(remark) {
  const raw = String(remark || '');
  const m = raw.match(/^\[SASMETA\](\{[\s\S]*?\})(?:\n([\s\S]*))?$/);
  if (!m) return { meta: {}, note: raw };
  try {
    return { meta: JSON.parse(m[1]), note: m[2] || '' };
  } catch {
    return { meta: {}, note: raw };
  }
}

function staffName(u) {
  return `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || u?.email || '';
}

export default function StockRecordForm({
  mode = 'in',
  items = [],
  projects = [],
  editData = null,
  onClose,
  onSaved,
}) {
  const { meta: editMeta, note: editNote } = decodeMovementMeta(editData?.remark);
  const selectedItem = items.find((i) => i.id === editData?.itemId) || null;
  const [staff, setStaff] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    itemId: editData?.itemId || '',
    quantity: editData?.quantityValue?.toString() || '',
    unitCost:
      editMeta.unitCost != null
        ? String(editMeta.unitCost)
        : selectedItem?.perItemCost != null
          ? String(selectedItem.perItemCost)
          : '',
    supplier: editMeta.supplier || '',
    poNumber: editMeta.poNumber || '',
    destinationType: editMeta.destinationType || 'Warehouse',
    site: editMeta.site || editData?.project || WAREHOUSE,
    fromSite: editMeta.fromSite || WAREHOUSE,
    toSite: editMeta.toSite || '',
    receivedBy: editData?.assignedBy || editMeta.receivedBy || '',
    employee: editData?.assignedBy || editMeta.employee || '',
    issueTo: editMeta.issueTo || 'Staff',
    jobNumber: editMeta.jobNumber || '',
    date: (editData?.date ? String(editData.date).slice(0, 10) : '') || new Date().toISOString().slice(0, 10),
    reason: editMeta.reason || '',
    direction: editMeta.direction || 'decrease',
    note: editNote || '',
  }));

  useEffect(() => {
    graphqlRequest(USERS)
      .then((d) => setStaff(d?.getAllUser?.data || []))
      .catch(() => setStaff([]));
  }, []);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const product = items.find((i) => i.id === form.itemId);
  const unit = product?.unit || 'Each';
  const onHand = Number(product?.available_quantity ?? product?.quantity ?? 0);
  const qty = Number(form.quantity) || 0;
  const unitCost = Number(form.unitCost) || 0;
  const totalCost = qty * unitCost;
  const sites = useMemo(() => {
    const names = (projects || []).map((p) => p.name).filter(Boolean);
    return [WAREHOUSE, ...names.filter((n) => n !== WAREHOUSE)];
  }, [projects]);

  const header =
    mode === 'in'
      ? { title: 'Record Stock In', tone: 'in' }
      : mode === 'out'
        ? { title: 'Record Stock Out', tone: 'out' }
        : mode === 'transfer'
          ? { title: 'Record Transfer', tone: 'trf' }
          : { title: 'Record Adjustment', tone: 'adj' };

  const projectIdForSite = (siteName) => {
    const p = (projects || []).find((x) => x.name === siteName);
    return p?.id || undefined;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.itemId) {
      toast.error('Select a product');
      return;
    }
    if (qty <= 0) {
      toast.error('Quantity must be greater than 0');
      return;
    }
    if (mode === 'out' && qty > onHand) {
      toast.error(`Only ${onHand} ${unit} on hand`);
      return;
    }
    if (mode === 'out' && !form.reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    if (mode === 'transfer') {
      if (!form.fromSite || !form.toSite) {
        toast.error('Choose both From and To sites');
        return;
      }
      if (form.fromSite === form.toSite) {
        toast.error('From and To sites must be different');
        return;
      }
    }
    if (mode === 'adjust' && !form.reason) {
      toast.error('Choose an adjustment reason');
      return;
    }

    const meta = {
      unitCost: unitCost || undefined,
      supplier: form.supplier || undefined,
      poNumber: form.poNumber || undefined,
      destinationType: form.destinationType || undefined,
      site: form.site || undefined,
      fromSite: form.fromSite || undefined,
      toSite: form.toSite || undefined,
      receivedBy: form.receivedBy || undefined,
      employee: form.employee || undefined,
      issueTo: form.issueTo || undefined,
      jobNumber: form.jobNumber || undefined,
      reason: form.reason || undefined,
      direction: form.direction || undefined,
      unit,
      movementDate: form.date || undefined,
    };

    const action =
      mode === 'in'
        ? 'Add to Stock'
        : mode === 'out'
          ? 'Issued to Staff'
          : mode === 'transfer'
            ? 'Transfer'
            : form.direction === 'increase'
              ? 'Adjustment Increase'
              : 'Adjustment Decrease';

    const assignedBy =
      mode === 'in'
        ? form.receivedBy
        : mode === 'out' || mode === 'transfer' || mode === 'adjust'
          ? form.employee || form.receivedBy
          : '';

    const projectId =
      mode === 'transfer'
        ? projectIdForSite(form.toSite)
        : projectIdForSite(form.site);

    setSaving(true);
    try {
      const fresh = await graphqlRequest(FIND_ITEMS);
      const allowed = fresh?.findAllItem || items;
      const picked = allowed.find((i) => i.id === form.itemId);
      if (!picked) {
        toast.error(
          'This product is no longer available for your account. Refresh the page and select it again from Products.',
        );
        setSaving(false);
        return;
      }

      const payload = {
        itemId: form.itemId,
        quantityAssigned: Math.round(qty) || 1,
        assignedBy: assignedBy || undefined,
        remark: encodeMovementMeta(meta, form.note),
        updateAction: action,
        projectId: projectId || undefined,
      };
      if (editData?.id) {
        await graphqlRequest(UPDATE, {
          updateInventoryManagementInput: { id: editData.id, ...payload },
        });
        toast.success('Movement updated');
      } else {
        await graphqlRequest(CREATE, { createInventoryManagementInput: payload });
        toast.success(
          mode === 'in'
            ? 'Stock in recorded'
            : mode === 'out'
              ? 'Stock out recorded'
              : mode === 'transfer'
                ? 'Transfer recorded'
                : 'Adjustment recorded',
        );
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="srf-overlay" onClick={onClose}>
      <div className={`srf-modal srf-modal--${header.tone}`} onClick={(e) => e.stopPropagation()}>
        <header className={`srf-header srf-header--${header.tone}`}>
          <h2>{editData ? `Edit ${header.title.replace('Record ', '')}` : header.title}</h2>
          <button type="button" className="srf-close" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </header>

        <form className="srf-body" onSubmit={submit}>
          {mode === 'in' && (
            <>
              <section>
                <h3><FaArrowDown /> Stock In Details</h3>
                <label className="srf-field srf-field--full">
                  <span>Product <em>*</em></span>
                  <select value={form.itemId} onChange={(e) => {
                    const id = e.target.value;
                    const it = items.find((x) => x.id === id);
                    setForm((prev) => ({
                      ...prev,
                      itemId: id,
                      unitCost: it?.perItemCost != null ? String(it.perItemCost) : prev.unitCost,
                      supplier: it?.supplier || prev.supplier,
                    }));
                  }} required>
                    <option value="">Select product</option>
                    {items.map((it) => (
                      <option key={it.id} value={it.id}>
                        {it.name} {it.code ? `(${it.code})` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="srf-grid">
                  <label className="srf-field">
                    <span>Quantity <em>*</em></span>
                    <input type="number" min="1" step="1" value={form.quantity} onChange={(e) => set('quantity', e.target.value)} required />
                  </label>
                  <label className="srf-field">
                    <span>Unit</span>
                    <input value={unit} readOnly />
                  </label>
                  <label className="srf-field">
                    <span>Cost Per Unit</span>
                    <input type="number" min="0" step="0.01" value={form.unitCost} onChange={(e) => set('unitCost', e.target.value)} />
                  </label>
                  <label className="srf-field">
                    <span>Total Cost</span>
                    <input value={`$${totalCost.toFixed(2)}`} readOnly />
                  </label>
                  <label className="srf-field">
                    <span>Supplier</span>
                    <input value={form.supplier} onChange={(e) => set('supplier', e.target.value)} placeholder="Supplier name" />
                  </label>
                  <label className="srf-field">
                    <span>Purchase Order #</span>
                    <input value={form.poNumber} onChange={(e) => set('poNumber', e.target.value)} placeholder="PO-2026-xxx (optional)" />
                  </label>
                </div>
              </section>
              <section>
                <h3><FaBuilding /> Destination</h3>
                <div className="srf-grid">
                  <label className="srf-field">
                    <span>Destination Type <em>*</em></span>
                    <select value={form.destinationType} onChange={(e) => set('destinationType', e.target.value)}>
                      <option>Warehouse</option>
                      <option>Job Site</option>
                    </select>
                  </label>
                  <label className="srf-field">
                    <span>Site / Location</span>
                    <select value={form.site} onChange={(e) => set('site', e.target.value)}>
                      {sites.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </label>
                  <label className="srf-field">
                    <span>Received By <em>*</em></span>
                    <select value={form.receivedBy} onChange={(e) => set('receivedBy', e.target.value)} required>
                      <option value="">Select staff</option>
                      {staff.map((u) => (
                        <option key={u.id} value={staffName(u)}>{staffName(u)}</option>
                      ))}
                    </select>
                  </label>
                  <label className="srf-field">
                    <span>Date Received <em>*</em></span>
                    <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} required />
                  </label>
                </div>
              </section>
              <section>
                <h3><FaFileAlt /> Notes</h3>
                <label className="srf-field srf-field--full">
                  <span>Reference / PO Number</span>
                  <input value={form.poNumber} onChange={(e) => set('poNumber', e.target.value)} placeholder="PO reference" />
                </label>
                <label className="srf-field srf-field--full">
                  <span>Remark</span>
                  <textarea rows={3} value={form.note} onChange={(e) => set('note', e.target.value)} placeholder="Optional note" />
                </label>
              </section>
            </>
          )}

          {mode === 'out' && (
            <>
              <section>
                <h3><FaArrowUp /> What’s Being Issued</h3>
                <label className="srf-field srf-field--full">
                  <span>Product <em>*</em></span>
                  <select value={form.itemId} onChange={(e) => set('itemId', e.target.value)} required>
                    <option value="">Select product</option>
                    {items.map((it) => (
                      <option key={it.id} value={it.id}>
                        {it.name} {it.code ? `(${it.code})` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="srf-grid">
                  <label className="srf-field">
                    <span>Quantity <em>*</em></span>
                    <input type="number" min="1" step="1" value={form.quantity} onChange={(e) => set('quantity', e.target.value)} required />
                  </label>
                  <div className="srf-field">
                    <span>Available Balance</span>
                    <div className="srf-balance">{onHand} {unit}</div>
                  </div>
                </div>
              </section>
              <section>
                <h3><FaBuilding /> Where It’s Going</h3>
                <div className="srf-grid">
                  <label className="srf-field">
                    <span>Issue To <em>*</em></span>
                    <select value={form.issueTo} onChange={(e) => set('issueTo', e.target.value)}>
                      <option>Staff</option>
                      <option>Job Site</option>
                      <option>Vehicle</option>
                    </select>
                  </label>
                  <label className="srf-field">
                    <span>Site</span>
                    <select value={form.site} onChange={(e) => set('site', e.target.value)}>
                      {sites.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </label>
                  <label className="srf-field">
                    <span>Employee</span>
                    <select value={form.employee} onChange={(e) => set('employee', e.target.value)}>
                      <option value="">Select employee</option>
                      {staff.map((u) => (
                        <option key={u.id} value={staffName(u)}>{staffName(u)}</option>
                      ))}
                    </select>
                  </label>
                  <label className="srf-field">
                    <span>Job # (optional)</span>
                    <input value={form.jobNumber} onChange={(e) => set('jobNumber', e.target.value)} placeholder="JOB-xxxx" />
                  </label>
                  <label className="srf-field srf-field--full">
                    <span>Date Issued <em>*</em></span>
                    <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} required />
                  </label>
                </div>
              </section>
              <section>
                <h3><FaFileAlt /> Reason</h3>
                <label className="srf-field srf-field--full">
                  <span>Reason <em>*</em></span>
                  <select value={form.reason} onChange={(e) => set('reason', e.target.value)} required>
                    <option value="">Select reason</option>
                    <option>Job use</option>
                    <option>Issued to staff</option>
                    <option>Consumed on site</option>
                    <option>Replacement</option>
                    <option>Other</option>
                  </select>
                </label>
                <label className="srf-field srf-field--full">
                  <span>Note</span>
                  <textarea rows={3} value={form.note} onChange={(e) => set('note', e.target.value)} placeholder="Optional note" />
                </label>
              </section>
            </>
          )}

          {mode === 'transfer' && (
            <>
              <section>
                <h3><FaBoxOpen /> Transfer Details</h3>
                <label className="srf-field srf-field--full">
                  <span>Product <em>*</em></span>
                  <select value={form.itemId} onChange={(e) => set('itemId', e.target.value)} required>
                    <option value="">Select product</option>
                    {items.map((it) => (
                      <option key={it.id} value={it.id}>
                        {it.name} {it.code ? `(${it.code})` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="srf-grid">
                  <label className="srf-field">
                    <span>Quantity <em>*</em></span>
                    <input type="number" min="1" value={form.quantity} onChange={(e) => set('quantity', e.target.value)} required />
                  </label>
                  <label className="srf-field">
                    <span>Unit</span>
                    <input value={unit} readOnly />
                  </label>
                  <label className="srf-field">
                    <span>From site <em>*</em></span>
                    <select value={form.fromSite} onChange={(e) => set('fromSite', e.target.value)}>
                      {sites.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </label>
                  <label className="srf-field">
                    <span>To site <em>*</em></span>
                    <select value={form.toSite} onChange={(e) => set('toSite', e.target.value)}>
                      <option value="">Select destination</option>
                      {sites.filter((s) => s !== form.fromSite).map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </label>
                  <label className="srf-field">
                    <span>Moved by</span>
                    <select value={form.employee} onChange={(e) => set('employee', e.target.value)}>
                      <option value="">Select staff</option>
                      {staff.map((u) => (
                        <option key={u.id} value={staffName(u)}>{staffName(u)}</option>
                      ))}
                    </select>
                  </label>
                  <label className="srf-field">
                    <span>Date <em>*</em></span>
                    <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} required />
                  </label>
                </div>
                <p className="srf-hint">
                  A transfer moves stock from one site to another. Company on-hand quantity does not change.
                </p>
              </section>
              <section>
                <h3><FaFileAlt /> Notes</h3>
                <label className="srf-field srf-field--full">
                  <span>Remark</span>
                  <textarea rows={3} value={form.note} onChange={(e) => set('note', e.target.value)} placeholder="e.g. Site transfer for weekend jobs" />
                </label>
              </section>
            </>
          )}

          {mode === 'adjust' && (
            <>
              <section>
                <h3><FaBoxOpen /> Adjustment</h3>
                <p className="srf-hint">
                  Use this when the count on the shelf does not match the system — damage, shrinkage, a recount, or stock found that was never booked in.
                </p>
                <label className="srf-field srf-field--full">
                  <span>Product <em>*</em></span>
                  <select value={form.itemId} onChange={(e) => set('itemId', e.target.value)} required>
                    <option value="">Select product</option>
                    {items.map((it) => (
                      <option key={it.id} value={it.id}>
                        {it.name} {it.code ? `(${it.code})` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="srf-grid">
                  <label className="srf-field">
                    <span>Direction <em>*</em></span>
                    <select value={form.direction} onChange={(e) => set('direction', e.target.value)}>
                      <option value="increase">Increase (found extra / recount up)</option>
                      <option value="decrease">Decrease (damage, loss, recount down)</option>
                    </select>
                  </label>
                  <label className="srf-field">
                    <span>Quantity <em>*</em></span>
                    <input type="number" min="1" value={form.quantity} onChange={(e) => set('quantity', e.target.value)} required />
                  </label>
                  <div className="srf-field">
                    <span>Current on-hand</span>
                    <div className="srf-balance">{onHand} {unit}</div>
                  </div>
                  <label className="srf-field">
                    <span>Site</span>
                    <select value={form.site} onChange={(e) => set('site', e.target.value)}>
                      {sites.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </label>
                  <label className="srf-field srf-field--full">
                    <span>Reason <em>*</em></span>
                    <select value={form.reason} onChange={(e) => set('reason', e.target.value)} required>
                      <option value="">Select reason</option>
                      <option>Count correction</option>
                      <option>Damaged</option>
                      <option>Expired / unusable</option>
                      <option>Lost / shrinkage</option>
                      <option>Found extra stock</option>
                      <option>Other</option>
                    </select>
                  </label>
                </div>
              </section>
              <section>
                <h3><FaFileAlt /> Notes</h3>
                <label className="srf-field srf-field--full">
                  <span>Remark</span>
                  <textarea rows={3} value={form.note} onChange={(e) => set('note', e.target.value)} placeholder="Explain the count difference" />
                </label>
              </section>
            </>
          )}

          <div className="srf-footer">
            <button type="button" className="srf-btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className={`srf-btn srf-btn--${header.tone}`} disabled={saving}>
              {saving ? 'Saving…' : editData ? 'Save changes' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
